/*
 * Copyright (c) 2026 Macrofocus GmbH. All Rights Reserved.
 */
package com.macrofocus.high_d.parallelcoordinates

import com.macrofocus.common.format.CPFormat
import com.macrofocus.common.format.FormatFactory
import com.macrofocus.common.properties.SimpleProperty
import com.macrofocus.high_d.axis.AxisListener
import com.macrofocus.high_d.axis.AxisModel
import org.mkui.component.CPComponentWrapper
import org.mkui.component.input.CPTextField
import org.mkui.graphics.colortheme.ColorTheme

/**
 * The maximum (above the slider) or the minimum (below it) of an axis, in a font a bit smaller than the one of the
 * headers, as the JFormattedTextFields of SwingAxisComponents: editable for numerical axes, a new limit changing the
 * range of the axis (and reducing the range of the slider to it).
 */
class AxisLimitView<Row, Column> private constructor(
    val axisModel: AxisModel<Row, Column>,
    val maximum: Boolean,
    private val colorTheme: () -> ColorTheme?,
    private val field: CPTextField,
) : CPComponentWrapper(field) {
    constructor(axisModel: AxisModel<Row, Column>, maximum: Boolean, colorTheme: () -> ColorTheme?) :
        this(axisModel, maximum, colorTheme, CPTextField(SimpleProperty.newInstance("")))

    private val axisListener = object : AxisListener {
        override fun axisChanged() {
            update()
        }
    }

    /**
     * The formatted limit, as shown: with the format of the axis, or for a numerical axis without one grouped by
     * thousands as NumberFormat.getNumberInstance (as the fields of the Java Compose parallel coordinates).
     */
    fun text(): String = if (axisModel.isNumerical && axisModel.format == null) {
        NUMBER_FORMAT.format(if (maximum) axisModel.maximum else axisModel.minimum) ?: ""
    } else if (maximum) axisModel.formattedMaximum else axisModel.formattedMinimum

    /** Shows the current limit, in the text color of the color theme. */
    fun update() {
        field.isEditable = axisModel.isNumerical
        field.foreground = colorTheme()?.text
        field.text = text()
    }

    private fun commit(text: String) {
        val value = parse(text) ?: return
        val interval = axisModel.interval
        if (maximum) {
            if (interval.end > value) {
                interval.setValue(interval.start, value - interval.start)
            }
            axisModel.maximum = value
        } else {
            if (interval.start < value) {
                interval.setValue(value, interval.end - value)
            }
            axisModel.minimum = value
        }
    }

    /** The number in [text], read with the format of the axis or as a plain number with grouping separators. */
    private fun parse(text: String): Double? {
        val format = axisModel.format
        if (format != null) {
            val value = runCatching { format.parse(text) }.getOrNull()
            if (value is Number) {
                return value.toDouble()
            }
        }
        return text.trim().replace("'", "").replace("’", "").replace(",", "").replace(" ", "").toDoubleOrNull()
    }

    init {
        field.fontSize = 11f
        field.setCommitListener { commit(it) }
        update()
        axisModel.addAxisListener(axisListener)
    }

    fun dispose() {
        axisModel.removeAxisListener(axisListener)
    }

    private companion object {
        val NUMBER_FORMAT: CPFormat<Any?> by lazy { FormatFactory.instance.createDecimalFormat() }
    }
}
